import { Css, IconButton } from "@homebound/beam";
import { ReactNode, useEffect, useState } from "react";
import { UppyUploader } from "src/components";
import { AssetUppyFile } from "src/components/uploader/UppyUploader";
import { readFileAsDataURL } from "src/components/uploader/utils";
import { ReadyPlanAssetType } from "src/generated/graphqlTypes";
import { sanitizeHtml } from "src/utils";

type FloorPlanAssetType =
  | ReadyPlanAssetType.FloorPlan
  | ReadyPlanAssetType.GroundBasementFloorPlan
  | ReadyPlanAssetType.FirstFloorPlan
  | ReadyPlanAssetType.SecondFloorPlan
  | ReadyPlanAssetType.AduFirstFloorPlan
  | ReadyPlanAssetType.AduSecondFloorPlan;

const floorPlanAssetTypes: ReadyPlanAssetType[] = [
  ReadyPlanAssetType.FloorPlan,
  ReadyPlanAssetType.GroundBasementFloorPlan,
  ReadyPlanAssetType.FirstFloorPlan,
  ReadyPlanAssetType.SecondFloorPlan,
  ReadyPlanAssetType.AduFirstFloorPlan,
  ReadyPlanAssetType.AduSecondFloorPlan,
];

type PlanPackageAssetUploaderProps = {
  // NOTE: If working with floor plan asset types, this needs to be a downloadUrl.
  assetUrl: string | null | undefined;
  saveAsset: (file: AssetUppyFile | undefined) => void;
  disabled?: ReactNode;
  assetType: ReadyPlanAssetType.CoverPhoto | FloorPlanAssetType;
};

export function PlanPackageAssetUploader(props: PlanPackageAssetUploaderProps) {
  const { assetUrl, saveAsset, disabled, assetType } = props;
  // Holds a base64 string of the local file of the uploaded image in order to immediately display it.
  const [localAsset, setLocalAsset] = useState<string>();
  const [svgText, setSvgText] = useState<string>();
  const imageToDisplay = localAsset ?? assetUrl;

  const ppUploaderConfig =
    assetType === ReadyPlanAssetType.CoverPhoto
      ? {
          allowedFileTypes: ["image/*"],
          dragDropText: "Drag & Drop or Click to Upload Cover Photo",
          // Setting the <img>'s width as defined on the asset resizer request.
          // This ensures consistent rendering between the local & remote assets
          preview: <img src={imageToDisplay ?? ""} alt="Cover" css={Css.maxwPx(321).$} loading="lazy" />,
        }
      : {
          allowedFileTypes: ["image/svg+xml"],
          dragDropText: "Drag & Drop or Click to Upload Floor Plan",
          // Show the pre existing svg we fetched otherwise the newly uploaded asset.
          preview: svgText ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(svgText) }} css={Css.w100.h100.$} />
          ) : (
            <img src={imageToDisplay ?? ""} alt="Floor Plan" css={Css.maxwPx(321).$} loading="lazy" />
          ),
        };

  useEffect(() => {
    if (!assetUrl || !floorPlanAssetTypes.includes(assetType)) return;
    // fetch the .svg file content and inject into page via state.
    void (async () => {
      // NOTE: This here is why we use `Asset.downloadUrl` for svg files since
      // previewUrl doesn't work with svg. We're fetching it explicitly here.
      const svgText = await (await fetch(assetUrl)).text();
      setSvgText(svgText);
    })();
  }, [assetType, assetUrl]);

  return imageToDisplay ? (
    <div css={Css.p2.relative.ba.bcGray100.br4.bw2.w100.mhPx(60).df.aic.jcc.$}>
      {ppUploaderConfig.preview}
      <div css={Css.absolute.top1.right1.$}>
        <IconButton
          inc={5}
          data-testid="exit"
          icon="x"
          onClick={() => {
            // Unset the asset from local state
            setLocalAsset(undefined);
            saveAsset(undefined);
          }}
          disabled={disabled}
        />
      </div>
    </div>
  ) : (
    !disabled && (
      <UppyUploader
        allowedFileTypes={ppUploaderConfig.allowedFileTypes}
        onFinish={async (uppyFile: AssetUppyFile) => {
          const result = await readFileAsDataURL(uppyFile.data as Blob);
          setLocalAsset(result);
          saveAsset(uppyFile);
        }}
        dragDropText={ppUploaderConfig.dragDropText}
        maxNumberOfFiles={1}
        dragDropWidth="100%"
        dragDropHeight={60}
      />
    )
  );
}
